import { z } from 'zod';
/**
 * What a meal planned without a recipe is written with.
 *
 * Kept apart from `./meal.ts` and free of any relative import: the
 * `meal-plans` function loads this file, and neither Deno nor the Supabase CLI
 * — which mounts every file a function imports when the stack starts —
 * resolves the `.js` specifiers TypeScript requires.
 */
/** Longest name of a free meal, as `meals_title_length` checks it. */
export const FREE_MEAL_TITLE_MAX = 80;
/** Most lines a free meal puts on the shopping list (`meals_shopping_items_count`). */
export const FREE_MEAL_ITEMS_MAX = 30;
/** Longest single line to buy for a free meal. */
export const FREE_MEAL_ITEM_MAX = 80;
/** Portions a meal can cook for the freezer: `meals.freezer_portions` is checked the same. */
export const FREEZER_PORTIONS_MAX = 99;
/** The name of a free meal: « Apéro dînatoire », « Salade des restes ». */
export const FreeMealTitleSchema = z.string().trim().min(1).max(FREE_MEAL_TITLE_MAX);
/** What to buy for it, one free line each: « Gâteaux apéro », « Charbon ». */
export const FreeMealItemsSchema = z
    .array(z.string().trim().min(1).max(FREE_MEAL_ITEM_MAX))
    .max(FREE_MEAL_ITEMS_MAX);
// ---------------------------------------------------------------------------
// Payloads of the `meal-plans` routes
//
// Shared with the app, which writes some meals straight through PostgREST
// (`apps/front/src/lib/api.ts`) and validates them with the same rules.
// ---------------------------------------------------------------------------
export const CreateMealSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
    meal_type: z.enum(['lunch', 'dinner']),
    /** Absent or null: the slot holds a free meal, or a table and no dish yet. */
    recipe_id: z.string().uuid().nullable().optional(),
    /**
     * Name of a meal planned without a recipe. A `recipe_id` on the same row
     * wins: the database clears the free meal it replaces.
     */
    title: FreeMealTitleSchema.nullable().optional(),
    /** What to buy for that free meal, one free line each. */
    shopping_items: FreeMealItemsSchema.optional(),
    /** Family member ids (`family_members.id`), so members without an account count too. */
    member_ids: z.array(z.string().uuid()).default([]),
    guest_count: z.number().int().min(0).default(0),
    /**
     * Portions cooked for the freezer, on top of the tables. Left out, a row
     * keeps what it had; the caller re-states `servings` with them.
     */
    freezer_portions: z.number().int().min(0).max(FREEZER_PORTIONS_MAX).optional(),
    /**
     * True once somebody set the table by hand: `member_ids` then overrides the
     * members' usual weeks. Left out, a row keeps what it had.
     */
    attendance_manual: z.boolean().optional(),
    servings: z.number().positive().optional(),
    notes: z.string().optional(),
    source_meal_id: z.string().uuid().nullable().optional(),
});
export const UpdateMealSchema = CreateMealSchema.partial();
export const UpsertMealsSchema = z.object({
    meals: z.array(CreateMealSchema).min(1),
});
/** The two meals whose slots are exchanged. */
export const SwapMealsSchema = z.object({
    a_id: z.string().uuid(),
    b_id: z.string().uuid(),
});
export const ListMealsSchema = z.object({
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const DaysSchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
});
//# sourceMappingURL=meal-requests.js.map